import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Play, Square, Coffee, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const TimeTracking = () => {
  const { user } = useContext(AuthContext);
  const [activeEntry, setActiveEntry] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [activity, setActivity] = useState('');
  const [notes, setNotes] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [breakStart, setBreakStart] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let interval;
    if (activeEntry) {
      interval = setInterval(() => {
        const start = new Date(activeEntry.start_time);
        const now = new Date();
        const diff = Math.floor((now - start) / 1000);
        setElapsedTime(diff);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeEntry]);

  const fetchData = async () => {
    try {
      const [statsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/projects`)
      ]);
      
      setActiveEntry(statsRes.data.active_entry);
      setProjects(projectsRes.data);
      
      if (statsRes.data.active_entry) {
        setSelectedProject(statsRes.data.active_entry.project_id || '');
        setActivity(statsRes.data.active_entry.activity_description || '');
        setNotes(statsRes.data.active_entry.notes || '');
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    }
  };

  const handleClockIn = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/time-entries`, {
        project_id: selectedProject || null,
        activity_description: activity || null,
        notes: notes || null
      });
      toast.success('Erfolgreich eingestempelt!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Einstempeln');
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/time-entries/${activeEntry.id}/clock-out`);
      toast.success('Erfolgreich ausgestempelt!');
      setActiveEntry(null);
      setSelectedProject('');
      setActivity('');
      setNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ausstempeln');
    } finally {
      setLoading(false);
    }
  };

  const handleStartBreak = () => {
    setBreakStart(new Date());
    toast.info('Pause gestartet');
  };

  const handleEndBreak = async () => {
    if (!breakStart) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/time-entries/${activeEntry.id}/add-break`, {
        start_time: breakStart.toISOString(),
        end_time: new Date().toISOString()
      });
      toast.success('Pause gespeichert!');
      setBreakStart(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern der Pause');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEntry = async () => {
    if (!activeEntry) return;
    
    setLoading(true);
    try {
      await axios.put(`${API}/time-entries/${activeEntry.id}`, {
        project_id: selectedProject || null,
        activity_description: activity || null,
        notes: notes || null
      });
      toast.success('Eintrag aktualisiert!');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="time-tracking-page">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Zeiterfassung</h1>
          <p className="text-gray-600 mt-2">Erfassen Sie Ihre Arbeitszeiten</p>
        </div>

        {/* Active Timer */}
        {activeEntry && (
          <Card className="border-l-4 border-l-green-500 bg-gradient-to-br from-green-50 to-emerald-50" data-testid="active-timer">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-green-500 p-4 rounded-full animate-pulse">
                    <Clock className="w-10 h-10 text-white" />
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-2">Aktive Zeiterfassung</p>
                <div className="text-7xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }} data-testid="timer-display">
                  {formatTime(elapsedTime)}
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Gestartet um {format(new Date(activeEntry.start_time), 'HH:mm', { locale: de })} Uhr
                </p>

                {/* Breaks */}
                {activeEntry.breaks && activeEntry.breaks.length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm text-gray-600 mb-2">Pausen:</p>
                    <div className="flex justify-center gap-2">
                      {activeEntry.breaks.map((b, idx) => (
                        <span key={idx} className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                          {b.duration_minutes?.toFixed(0)} Min
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-center gap-4">
                  {!breakStart ? (
                    <Button
                      onClick={handleStartBreak}
                      variant="outline"
                      className="bg-yellow-50 border-yellow-300 hover:bg-yellow-100"
                      data-testid="start-break-button"
                    >
                      <Coffee className="w-4 h-4 mr-2" />
                      Pause starten
                    </Button>
                  ) : (
                    <Button
                      onClick={handleEndBreak}
                      variant="outline"
                      className="bg-yellow-100 border-yellow-400 hover:bg-yellow-200"
                      disabled={loading}
                      data-testid="end-break-button"
                    >
                      <Coffee className="w-4 h-4 mr-2" />
                      Pause beenden
                    </Button>
                  )}
                  <Button
                    onClick={handleClockOut}
                    size="lg"
                    className="bg-red-600 hover:bg-red-700"
                    disabled={loading}
                    data-testid="clock-out-button"
                  >
                    <Square className="w-4 h-4 mr-2" />
                    Ausstempeln
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Clock In / Entry Form */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle>{activeEntry ? 'Eintrag bearbeiten' : 'Neue Zeiterfassung starten'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="project">Projekt (optional)</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger data-testid="project-select">
                  <SelectValue placeholder="Projekt auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Projekt</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="activity">Tätigkeitsbeschreibung</Label>
              <Textarea
                id="activity"
                placeholder="Was arbeiten Sie gerade?"
                value={activity}
                onChange={(e) => setActivity(e.target.value)}
                rows={3}
                data-testid="activity-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notizen / Kommentare</Label>
              <Textarea
                id="notes"
                placeholder="Zusätzliche Notizen..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                data-testid="notes-input"
              />
            </div>

            {activeEntry ? (
              <Button
                onClick={handleUpdateEntry}
                className="w-full"
                disabled={loading}
                data-testid="update-entry-button"
              >
                Eintrag aktualisieren
              </Button>
            ) : (
              <Button
                onClick={handleClockIn}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                disabled={loading}
                data-testid="clock-in-button"
              >
                <Play className="w-4 h-4 mr-2" />
                Einstempeln
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default TimeTracking;
